import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleDot, Plug, Plus, Search } from 'lucide-react';
import {
  EmptyState,
  GlassCard,
  MarketplaceIcon,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import {
  SEED_INSTALLED_CONNECTORS,
  SEED_MARKETPLACE_CONNECTORS,
  type InstalledConnectorSeed,
  type MarketplaceConnectorSeed,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';

const MCP_STATUS_DOT: Record<string, string> = {
  connected: 'bg-emerald-500',
  pending: 'bg-amber-400',
  'needs-auth': 'bg-amber-500',
  failed: 'bg-rose-500',
  disabled: 'bg-zinc-400 dark:bg-zinc-600',
};

const FRESHLY_INSTALLED_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

function isAgedPastGrace(installedAt: number, now = Date.now()): boolean {
  return now - installedAt > FRESHLY_INSTALLED_GRACE_MS;
}

export function ConnectorsScene() {
  const [installed, setInstalled] = useState<InstalledConnectorSeed[]>(
    () => [...SEED_INSTALLED_CONNECTORS],
  );
  const [marketplace, setMarketplace] = useState<MarketplaceConnectorSeed[]>(
    () => [...SEED_MARKETPLACE_CONNECTORS],
  );
  const [marketplaceQuery, setMarketplaceQuery] = useState('');

  const filteredMarketplace = useMemo(() => {
    const needle = marketplaceQuery.trim().toLowerCase();
    if (!needle) return marketplace;
    return marketplace.filter((entry) =>
      `${entry.name} ${entry.publisher} ${entry.category}`.toLowerCase().includes(needle),
    );
  }, [marketplace, marketplaceQuery]);

  const installedByMarketplaceId = useMemo(
    () =>
      Object.fromEntries(
        installed
          .filter((connector) => connector.marketplaceId)
          .map((connector) => [connector.marketplaceId, connector.id]),
      ),
    [installed],
  );

  const sortedInstalled = useMemo(
    () =>
      [...installed].sort((a, b) => {
        if (a.invocations !== b.invocations) return b.invocations - a.invocations;
        return a.name.localeCompare(b.name);
      }),
    [installed],
  );

  const handleInstall = (entry: MarketplaceConnectorSeed) => {
    if (installedByMarketplaceId[entry.id]) return;
    const newConnector: InstalledConnectorSeed = {
      id: `user-conn-${entry.id}`,
      marketplaceId: entry.id,
      name: entry.name,
      description: entry.description,
      enabled: true,
      mcpStatus: 'pending',
      needsReauth: false,
      invocations: 0,
      installedAt: Date.now(),
      transport: entry.transport,
      logoUrl: entry.logoUrl,
      logoInvertInDark: entry.logoInvertInDark,
    };
    setInstalled((prev) => [...prev, newConnector]);
    setMarketplace((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, installed: true } : e)),
    );
  };

  return (
    <SurfacePageLayout width="content">
      <Commentable name="ConnectorsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <SettingsPageHeader
              title="Connectors"
              description="MCP servers the agent can use as tools and resources."
              actions={
                <a
                  href="#marketplace"
                  className="inline-flex items-center whitespace-nowrap gap-1.5 rounded-lg bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 dark:bg-accent-500 dark:text-white dark:hover:bg-accent-400"
                >
                  <Plus className="h-4 w-4" />
                  New connector
                </a>
              }
            />

            <Commentable name="InstalledList">
              <section>
                <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted">
                  Installed
                </h2>
                {sortedInstalled.length === 0 ? (
                  <EmptyState
                    icon={<Plug className="h-8 w-8 text-zinc-400" />}
                    title="No connectors yet"
                    description="Install one from the marketplace below or add your own."
                    action={
                      <a
                        href="#marketplace"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-2 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
                      >
                        <Plus className="h-4 w-4" />
                        Add your first connector
                      </a>
                    }
                  />
                ) : (
                  <GlassCard className="overflow-hidden">
                    {sortedInstalled.map((c, i) => {
                      let usageHint: string | null = null;
                      if (!c.builtin) {
                        if (c.invocations > 0) {
                          usageHint = `${c.invocations} call${c.invocations === 1 ? '' : 's'}`;
                        } else if (isAgedPastGrace(c.installedAt)) {
                          usageHint = 'unused';
                        }
                      }

                      const showMcpDot =
                        c.mcpStatus !== undefined &&
                        !(c.mcpStatus === 'needs-auth' && c.needsReauth);

                      return (
                        <Link
                          key={c.id}
                          to={`/settings/connectors/${c.id}`}
                          className={`flex items-center gap-4 px-5 py-4 transition-colors hover:bg-[var(--glass-surface-strong)] ${
                            i > 0 ? 'border-t border-[var(--glass-border)]' : ''
                          }`}
                        >
                          <CircleDot
                            className={`h-3.5 w-3.5 flex-shrink-0 ${
                              !c.enabled
                                ? 'text-zinc-300 dark:text-zinc-700'
                                : 'text-emerald-500'
                            }`}
                          />
                          <MarketplaceIcon
                            name={c.name}
                            logoUrl={c.logoUrl}
                            invertInDark={c.logoInvertInDark}
                            size={28}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium">{c.name}</span>
                              {showMcpDot ? (
                                <span
                                  className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${MCP_STATUS_DOT[c.mcpStatus] ?? 'bg-zinc-400'}`}
                                  title={`MCP status: ${c.mcpStatus}`}
                                  aria-label={`MCP status: ${c.mcpStatus}`}
                                />
                              ) : null}
                              <Pill>{c.transport}</Pill>
                              {c.builtin ? (
                                <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                                  Built-in
                                </span>
                              ) : null}
                              {c.needsReauth ? (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                                  Needs re-auth
                                </span>
                              ) : null}
                            </div>
                            {c.description ? (
                              <p className="mt-0.5 truncate text-sm text-muted">{c.description}</p>
                            ) : null}
                          </div>
                          <span className="flex-shrink-0 text-xs text-zinc-400 dark:text-zinc-500">
                            {formatRelativeTime(c.installedAt)}
                            {usageHint ? (
                              <>
                                {' · '}
                                {usageHint}
                              </>
                            ) : null}
                          </span>
                        </Link>
                      );
                    })}
                  </GlassCard>
                )}
              </section>
            </Commentable>

            <Commentable name="Marketplace">
              <section id="marketplace" className="scroll-mt-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold tracking-tight">Marketplace</h2>
                  <p className="mt-1 text-sm text-muted">
                    Curated remote MCP servers. One-click install with an API token — no local
                    setup.
                  </p>
                </div>
                <div className="relative mb-6">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="search"
                    value={marketplaceQuery}
                    onChange={(event) => setMarketplaceQuery(event.target.value)}
                    placeholder="Search by name, category, or tag…"
                    className="w-full rounded-lg border border-divider bg-input py-2 pl-9 pr-3 text-sm outline-none focus:border-input-focus"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredMarketplace.map((entry) => {
                    const installedConnectorId = installedByMarketplaceId[entry.id];
                    const isInstalled = Boolean(installedConnectorId);
                    return (
                      <GlassCard
                        key={entry.id}
                        className="flex flex-col p-5 transition-colors hover:bg-[var(--glass-surface-strong)]"
                      >
                        <div className="flex items-start gap-3">
                          <MarketplaceIcon
                            name={entry.name}
                            logoUrl={entry.logoUrl}
                            invertInDark={entry.logoInvertInDark}
                            size={40}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{entry.name}</div>
                            <div className="mt-0.5 truncate text-xs text-muted">
                              {[entry.publisher, entry.category].filter(Boolean).join(' · ') ||
                                'Community'}
                            </div>
                          </div>
                          <Pill className="flex-shrink-0">{entry.transport}</Pill>
                        </div>
                        <p className="mt-3 line-clamp-3 text-sm text-zinc-600 dark:text-zinc-400">
                          {entry.description}
                        </p>
                        <div className="mt-4 flex-1" />
                        {isInstalled ? (
                          <div className="flex items-center justify-between gap-3">
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                              Installed
                            </span>
                            <Link
                              to={`/settings/connectors/${installedConnectorId}`}
                              className="text-sm font-medium text-zinc-700 underline-offset-4 hover:underline dark:text-zinc-300"
                            >
                              Configure
                            </Link>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleInstall(entry)}
                            className="inline-flex items-center justify-center rounded-lg bg-zinc-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
                          >
                            Install
                          </button>
                        )}
                      </GlassCard>
                    );
                  })}
                </div>
              </section>
            </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
